import { Modal } from "antd";
import { ChevronDown, LogOut, Mail, Menu, ShieldCheck, UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { scRoundLogo, scWhiteTextLogo } from "../../../assets/images";
import { Page } from "../../../routes/config";
import { storage } from "../../../utils/storage";
import { UIAvatar } from "../avatar";
import UIBreadcrumb from "../breadcrumb";
import Button from "../button/button";
import { UIDropdown } from "../dropdown";
import "./style.css";

// Both logo assets are exported from the same artboard height, so giving them
// an equal CSS height (with width:auto) keeps their aspect ratios intact AND
// lines the wordmark up with the icon automatically. Plain <img> is used
// rather than UIImage because UIImage defaults to width:100%, which stretches
// the wordmark across the whole plate.
export const SmartCosmosLogo = ({ collapsed }: any) => (
  <Link to={Page.HOME} className="sclogoLink" aria-label="Smart Cosmos home">
    <div className="sclogo">
      <img className="sclogoMark" src={scRoundLogo} alt="Smart Cosmos" />
      {!collapsed && (
        <img className="sclogoWordmark" src={scWhiteTextLogo} alt="" />
      )}
    </div>
  </Link>
);

const ProfileField = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="profileField">
    <span className="profileFieldIcon">{icon}</span>
    <div className="profileFieldText">
      <div className="profileFieldLabel">{label}</div>
      <div className="profileFieldValue" title={value}>
        {value}
      </div>
    </div>
  </div>
);

const UIHeader = (props: any) => {
  const storedUser = useMemo(() => storage.getUser(), []);
  const user = storedUser ?? null;

  const { collapsed = false, setCollapsed, centerContent } = props ?? {};
  const [profileOpen, setProfileOpen] = useState(false);

  const displayName =
    (user?.name as string | undefined) ||
    (user?.username as string | undefined) ||
    (user?.userRole?.userName as string | undefined) ||
    "User";
  const roleName =
    (user?.userRole?.roleName as string | undefined) ||
    (user?.roleName as string | undefined) ||
    "—";
  const email = (user?.email as string | undefined) || "—";
  const username = (user?.username as string | undefined) || "—";
  const imageUrl = (user?.imageUrl as string | undefined) || "";

  const handleLogout = () => {
    storage.clearAll();
    window.location.assign(Page.LOGIN);
  };

  const userDropDownOptions = [
    {
      label: "My Profile",
      key: "view-profile",
      onClick: () => setProfileOpen(true),
      icon: (
        <div className="userDropdownIcon">
          <UserRound size={16} strokeWidth={2} />
        </div>
      ),
    },
    { type: "divider" },
    {
      label: "Logout",
      key: "logout",
      className: "headerMenuItem-logout",
      onClick: handleLogout,
      icon: (
        <div className="userDropdownIcon">
          <LogOut size={16} strokeWidth={2} />
        </div>
      ),
    },
  ];

  // Same icon-badge + title/subtitle header the other rich modals use, with
  // the UserRound icon already used for "My Profile" in the account menu.
  const profileModalTitle = (
    <div className="modalHeaderRich">
      <span className="modalHeaderIcon">
        <UserRound size={18} strokeWidth={2} />
      </span>
      <div className="modalHeaderText">
        <div className="modalHeaderTitle">My Profile</div>
        <div className="modalHeaderSubtitle">Your account details</div>
      </div>
    </div>
  );

  return (
    <>
      <div className="site-layout-background fixedHeader header">
        <div
          className={`logo ${collapsed ? "collapsedWidth" : "sidebarWidth"}`}
          style={{ textAlign: "center", transition: "width 0.2s ease" }}
        >
          <SmartCosmosLogo collapsed={collapsed} />
        </div>

        <div className="headerContent">
          <div className="headerLeftControls">
            <button
              type="button"
              className="hammerberg"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={() => {
                if (typeof setCollapsed === "function") setCollapsed(!collapsed);
              }}
            >
              <Menu size={20} strokeWidth={1.75} />
            </button>

            <div className="headerCenter">
              {centerContent ?? (
                <UIBreadcrumb
                  showBack={false}
                  items={[
                    { label: "Reports", to: Page.REPORTS_ENABLEMENT },
                    { label: "Dashboard" },
                  ]}
                />
              )}
            </div>
          </div>

          <div className="headerRightControls">
            <UIDropdown
              items={userDropDownOptions}
              placement="bottomRight"
              overlayClassName="headerAccountMenu"
            >
              <div className="profileImageSection" role="button" tabIndex={0}>
                <div className="profileImage">
                  {imageUrl ? (
                    <img alt="profile" src={imageUrl} />
                  ) : (
                    <UIAvatar text={displayName} type="username" />
                  )}
                </div>
                <div className="info">
                  <div className="name">{displayName}</div>
                  <div className="role">{roleName}</div>
                </div>
                <div className="icon">
                  <ChevronDown size={16} strokeWidth={2} />
                </div>
              </div>
            </UIDropdown>
          </div>
        </div>
      </div>

      <Modal
        title={profileModalTitle}
        open={profileOpen}
        onCancel={() => setProfileOpen(false)}
        width={520}
        wrapClassName="profileModal"
        footer={
          <Button variant="accent" size="sm" className="px-5" onClick={() => setProfileOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="profileSummary">
          {imageUrl ? (
            <img className="profileSummaryImage" alt="profile" src={imageUrl} />
          ) : (
            <UIAvatar text={displayName} type="username" height="64px" width="64px" size="20px" />
          )}
          <div className="profileSummaryText">
            <div className="profileSummaryName">{displayName}</div>
            <span className="profileRolePill">{roleName}</span>
          </div>
        </div>

        <div className="profileFields">
          <ProfileField icon={<UserRound size={15} strokeWidth={2} />} label="Username" value={username} />
          <ProfileField icon={<Mail size={15} strokeWidth={2} />} label="Email" value={email} />
          <ProfileField icon={<ShieldCheck size={15} strokeWidth={2} />} label="Role" value={roleName} />
        </div>
      </Modal>
    </>
  );
};

export default UIHeader;
